/**
 * Harmonic Drift - Node (Enhanced Melodic Physics)
 * Represents a musical node with advanced physics-based melody generation
 */

import { audioEngine } from '../audio/AudioEngine.js';
import { CONFIG } from '../config/index.js';

export class Node {
    /**
     * Create a new Node
     * @param {number} id - Unique node ID
     * @param {Object} instDef - Instrument definition
     * @param {number} x - Initial X position
     * @param {number} y - Initial Y position
     * @param {number} canvasWidth - Canvas width
     * @param {number} canvasHeight - Canvas height
     */
    constructor(id, instDef, x, y, canvasWidth, canvasHeight) {
        this.id = id;
        this.instDef = instDef;
        this.x = x !== undefined ? x : Math.random() * canvasWidth;
        this.y = y !== undefined ? y : Math.random() * canvasHeight;
        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
        
        // Physics properties based on behavior
        const behavior = instDef.behavior || 'normal';
        let speedBase = 2;
        if (behavior === 'heavy' || behavior === 'floaty') speedBase = 1;
        
        this.vx = (Math.random() - 0.5) * speedBase;
        this.vy = (Math.random() - 0.5) * speedBase;
        
        // Previous velocity for acceleration calculation
        this.prevVx = this.vx;
        this.prevVy = this.vy;
        
        // Size based on behavior
        this.radius = 20;
        if (behavior === 'heavy') this.radius = 35;
        if (behavior === 'floaty') this.radius = 45;

        this.baseColor = instDef.color;
        this.pulse = 0;
        this.locked = false;
        this.lastNote = "";
        this.spawning = 1;
        
        // Controlable properties
        this.isFixed = false;
        this.isMuted = false;
        
        // Connection tracking
        this.connections = [];
        this.connectionCount = 0;
        
        // Looping pattern state
        this.isLooping = instDef.looping || false;
        this.patternIndex = 0;
        this.patternName = instDef.pattern || null;
        this.patternSpeed = instDef.patternSpeed || 1;
        this.stepAccumulator = 0;
        
        // Physics modulation state
        this.physicsModulation = instDef.physicsModulation || {};
        this.currentVelocity = 0;
        this.smoothedVelocity = 0;
        
        // === ENHANCED MELODIC STATE ===
        
        // Melodic memory - remembers recent notes for motif development
        this.melodicMemory = [];
        this.maxMelodicMemory = 8;
        
        // Movement direction tracking for melodic contour
        this.movementAngle = 0;
        this.smoothedMovementAngle = 0;
        
        // Acceleration for articulation
        this.acceleration = 0;
        this.smoothedAcceleration = 0;
        
        // Recent collision state for harmony triggers
        this.recentCollision = false;
        this.collisionPartner = null;
        this.collisionCooldown = 0;
        
        // Call and response state
        this.lastHeardNote = null;
        this.responseDelay = 0;
        this.isResponding = false;
        
        // Rhythmic density accumulator
        this.rhythmDensity = 0;
        this.baseTriggerRate = this.calculateBaseTriggerRate(behavior);
        
        // Harmonic gravity - tendency toward certain scale degrees
        this.harmonicGravity = this.calculateHarmonicGravity(behavior);
        
        // Trigger pattern for sequencing
        this.triggerPattern = new Array(16).fill(false);
        this.dynamicPattern = new Array(16).fill(0); // 0-1 probability pattern
        this.generatePattern(behavior);
    }

    /**
     * Calculate base trigger rate based on behavior
     */
    calculateBaseTriggerRate(behavior) {
        switch (behavior) {
            case 'heavy': return 0.5;
            case 'floaty': return 0.3;
            case 'agile': return 1.5;
            default: return 1.0;
        }
    }

    /**
     * Calculate harmonic gravity - tendency toward certain scale degrees
     */
    calculateHarmonicGravity(behavior) {
        // Returns weights for scale degrees 0-7
        switch (behavior) {
            case 'heavy':
                // Bass prefers root and fifth
                return [1.0, 0.2, 0.3, 0.4, 0.9, 0.3, 0.2, 0.5];
            case 'floaty':
                // Pads prefer thirds and sevenths for color
                return [0.7, 0.5, 0.9, 0.6, 0.7, 0.8, 0.9, 0.6];
            case 'agile':
                // Leads explore more freely
                return [0.8, 0.7, 0.8, 0.7, 0.8, 0.7, 0.7, 0.8];
            default:
                return [0.8, 0.6, 0.7, 0.6, 0.8, 0.6, 0.7, 0.7];
        }
    }

    /**
     * Generate a trigger pattern based on behavior
     */
    generatePattern(behavior) {
        const r = Math.random();
        if (behavior === 'heavy') {
            this.triggerPattern[0] = true;
            this.dynamicPattern[0] = 1.0;
            if (r > 0.5) {
                this.triggerPattern[8] = true;
                this.dynamicPattern[8] = 0.8;
            }
        } else if (behavior === 'floaty') {
            this.triggerPattern[0] = true;
            this.dynamicPattern[0] = 1.0;
        } else {
            const density = 4;
            for (let i = 0; i < density; i++) {
                const idx = Math.floor(Math.random() * 16);
                this.triggerPattern[idx] = true;
                this.dynamicPattern[idx] = 0.5 + Math.random() * 0.5;
            }
        }
    }

    /**
     * Update node position and state
     */
    update(mouse, physicsPaused, globalEntropy, globalSpeedMult) {
        if (this.spawning > 0) this.spawning -= 0.05;

        // Store previous velocity for acceleration calculation
        this.prevVx = this.vx;
        this.prevVy = this.vy;

        // Calculate current velocity magnitude
        this.currentVelocity = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        this.smoothedVelocity = this.smoothedVelocity * 0.9 + this.currentVelocity * 0.1;

        // Calculate movement direction
        if (this.currentVelocity > 0.1) {
            this.movementAngle = Math.atan2(this.vy, this.vx);
            this.smoothedMovementAngle = this.lerpAngle(
                this.smoothedMovementAngle, 
                this.movementAngle, 
                0.1
            );
        }

        if (this.locked && mouse) {
            this.x += (mouse.x - this.x) * 0.2;
            this.y += (mouse.y - this.y) * 0.2;
        } else if (!physicsPaused && !this.isFixed) {
            let speedMult = 1;
            if (globalEntropy > 0.5) speedMult = 1 + (globalEntropy * 2);
            speedMult *= globalSpeedMult;

            if (this.instDef.behavior === 'agile') speedMult *= 1.5;
            if (this.instDef.behavior === 'floaty') speedMult *= 0.5;

            this.x += this.vx * speedMult;
            this.y += this.vy * speedMult;

            if (this.x < this.radius || this.x > this.canvasWidth - this.radius) {
                this.vx *= -1;
            }
            if (this.y < this.radius || this.y > this.canvasHeight - this.radius) {
                this.vy *= -1;
            }
            
            this.x = Math.max(this.radius, Math.min(this.canvasWidth - this.radius, this.x));
            this.y = Math.max(this.radius, Math.min(this.canvasHeight - this.radius, this.y));
        }
        
        // Calculate acceleration
        const dvx = this.vx - this.prevVx;
        const dvy = this.vy - this.prevVy;
        this.acceleration = Math.sqrt(dvx * dvx + dvy * dvy);
        this.smoothedAcceleration = this.smoothedAcceleration * 0.85 + this.acceleration * 0.15;
        
        // Update collision cooldown
        if (this.collisionCooldown > 0) {
            this.collisionCooldown--;
            if (this.collisionCooldown === 0) {
                this.recentCollision = false;
                this.collisionPartner = null;
            }
        }
        
        // Update response delay
        if (this.responseDelay > 0) {
            this.responseDelay--;
        }
        
        // Decay pulse
        if (this.pulse > 0) this.pulse -= 0.05;
    }

    /**
     * Lerp between two angles properly handling wrapping
     */
    lerpAngle(a, b, t) {
        const diff = ((b - a + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
        return a + diff * t;
    }

    /**
     * Update connections with other nodes - enhanced with harmonic data
     */
    updateConnections(allNodes) {
        this.connections = [];
        
        for (const other of allNodes) {
            if (other.id === this.id) continue;
            
            const dx = other.x - this.x;
            const dy = other.y - this.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            
            if (dist < CONFIG.connectionDistance) {
                // Calculate relative movement direction
                const relativeAngle = Math.atan2(dy, dx);
                const approachAngle = Math.atan2(other.vy - this.vy, other.vx - this.vx);
                
                this.connections.push({
                    nodeId: other.id,
                    node: other,
                    distance: dist,
                    strength: 1 - (dist / CONFIG.connectionDistance),
                    otherInstrument: other.instDef,
                    relativeAngle: relativeAngle,
                    isApproaching: Math.abs(relativeAngle - approachAngle) > Math.PI / 2,
                    lastNote: other.lastNote,
                    otherVelocity: other.smoothedVelocity,
                    otherY: other.y
                });
            }
        }
        
        this.connectionCount = this.connections.length;
        
        // Listen for nearby notes (call and response)
        this.updateCallAndResponse();
    }

    /**
     * Update call and response listening
     */
    updateCallAndResponse() {
        if (!CONFIG.melodicPhysics.enableCallResponse) return;
        
        // Find the strongest connection that recently played
        for (const conn of this.connections) {
            if (conn.node.pulse > 0.8 && conn.strength > 0.5 && !this.isResponding) {
                this.lastHeardNote = conn.lastNote;
                this.responseDelay = Math.floor(4 + Math.random() * 4); // 4-8 ticks delay
                this.isResponding = true;
                break;
            }
        }
    }

    /**
     * Register a collision with another node
     */
    registerCollision(otherNode) {
        if (this.collisionCooldown === 0) {
            this.recentCollision = true;
            this.collisionPartner = otherNode;
            this.collisionCooldown = 8; // 8 ticks cooldown
        }
    }

    /**
     * Get physics modulation parameters - enhanced version
     */
    getPhysicsModulation() {
        const mod = CONFIG.physicsModulation;
        const melodic = CONFIG.melodicPhysics;
        const pmSettings = this.physicsModulation;
        
        // Normalize values
        const normalizedVelocity = Math.min(1, this.smoothedVelocity / 5);
        const normalizedX = this.x / this.canvasWidth;
        const normalizedY = this.y / this.canvasHeight;
        const normalizedConnections = Math.min(1, this.connectionCount / mod.maxConnections);
        const normalizedAcceleration = Math.min(1, this.smoothedAcceleration * 10);
        
        // Calculate connection strengths and harmonic info
        const avgConnectionStrength = this.connections.length > 0
            ? this.connections.reduce((sum, c) => sum + c.strength, 0) / this.connections.length
            : 0;
            
        // Calculate weighted pitch center from connections
        const connectionPitchInfluence = this.calculateConnectionPitchInfluence();
        
        // Movement direction melodic contour (-1 to 1, negative = descending)
        const melodicContour = Math.sin(this.smoothedMovementAngle);
        
        // Calculate dynamic rhythm density based on velocity
        const rhythmDensityMod = 1 + (normalizedVelocity * melodic.velocityRhythmFactor);
        
        return {
            // === Original parameters ===
            pitchShift: pmSettings.velocityToPitch 
                ? normalizedVelocity * mod.pitchRangeOctaves * 12 * mod.velocityPitchFactor
                : 0,
            
            detune: pmSettings.positionToDetune
                ? (normalizedX - 0.5) * 2 * mod.positionDetuneRange
                : 0,
            
            octaveShift: pmSettings.connectionsToOctave
                ? -Math.floor(normalizedConnections * 2)
                : 0,
            
            reverbBoost: pmSettings.connectionsToReverb
                ? normalizedConnections * mod.connectionReverbBoost
                : 0,
            
            filterMod: pmSettings.velocityToFilter
                ? normalizedVelocity
                : 0.5,
            
            volumeMod: pmSettings.velocityToVolume
                ? 0.5 + normalizedVelocity * 0.5
                : 1,
            
            attackMod: pmSettings.velocityToAttack
                ? 0.1 + (1 - normalizedVelocity) * 0.9
                : 1,
            
            harmonicityMod: pmSettings.positionToHarmonicity
                ? 1 + normalizedY * 4
                : null,
            
            modIndexMod: pmSettings.connectionsToModIndex
                ? 2 + avgConnectionStrength * 10
                : null,
            
            patternShift: pmSettings.connectionsToPattern
                ? Math.floor(normalizedConnections * 3)
                : 0,
            
            dampeningMod: pmSettings.velocityToDampening
                ? 2000 + normalizedVelocity * 4000
                : null,
            
            resonanceMod: pmSettings.connectionsToResonance
                ? 0.9 + avgConnectionStrength * 0.09
                : null,
            
            vibratoMod: pmSettings.velocityToVibratoAmount
                ? normalizedVelocity * 0.8
                : null,
            
            duoHarmonicityMod: pmSettings.connectionsToHarmonicity
                ? 1 + avgConnectionStrength * 2
                : null,
            
            // === NEW MELODIC PARAMETERS ===
            
            // Melodic contour from movement direction
            melodicContour: melodicContour,
            
            // Scale degree bias from position (X affects mode feel)
            scaleDegreeOffset: Math.floor(normalizedX * 3) - 1, // -1 to 2
            
            // Connection-based pitch influence
            connectionPitchInfluence: connectionPitchInfluence,
            
            // Acceleration affects note duration
            durationMod: 1 - (normalizedAcceleration * 0.5),
            
            // Rhythm density multiplier
            rhythmDensity: rhythmDensityMod,
            
            // Call and response state
            isResponding: this.isResponding && this.responseDelay === 0,
            responseNote: this.lastHeardNote,
            
            // Collision harmony trigger
            collisionTriggered: this.recentCollision,
            collisionPartner: this.collisionPartner,
            
            // Harmonic gravity weights for this node
            harmonicGravity: this.harmonicGravity,
            
            // Melodic memory for motif development
            recentNotes: [...this.melodicMemory],
            
            // Raw values
            velocity: normalizedVelocity,
            acceleration: normalizedAcceleration,
            positionX: normalizedX,
            positionY: normalizedY,
            connections: normalizedConnections,
            connectionStrength: avgConnectionStrength,
            movementAngle: this.smoothedMovementAngle
        };
    }

    /**
     * Calculate pitch influence from connected nodes
     */
    calculateConnectionPitchInfluence() {
        if (this.connections.length === 0) return null;
        
        let totalWeight = 0;
        let weightedY = 0;
        let intervals = [];
        
        for (const conn of this.connections) {
            const weight = conn.strength * conn.strength; // Square for stronger nearby influence
            totalWeight += weight;
            weightedY += conn.otherY * weight;
            
            // Collect intervals for harmony calculation
            if (conn.lastNote) {
                intervals.push({
                    note: conn.lastNote,
                    strength: conn.strength
                });
            }
        }
        
        return {
            pitchCenter: totalWeight > 0 ? weightedY / totalWeight : null,
            harmonicIntervals: intervals,
            totalStrength: totalWeight
        };
    }

    /**
     * Update canvas dimensions
     */
    updateDimensions(width, height) {
        this.canvasWidth = width;
        this.canvasHeight = height;
    }

    /**
     * Handle tick event from sequencer
     */
    onTick(time, beat) {
        if (this.isMuted) return;

        const modulation = this.getPhysicsModulation();
        
        // Update rhythm density based on velocity
        this.rhythmDensity += (modulation.rhythmDensity - 1) * 0.1;
        
        if (this.isLooping && this.patternName) {
            // Velocity-modified pattern speed
            const effectiveSpeed = this.patternSpeed * modulation.rhythmDensity;
            this.stepAccumulator += effectiveSpeed / 4;
            
            if (this.stepAccumulator >= 1) {
                this.stepAccumulator -= 1;
                this.triggerFromPattern(time, modulation);
            }
        } else {
            // Enhanced trigger probability based on velocity
            const baseTrigger = this.triggerPattern[beat % 16];
            const dynamicProb = this.dynamicPattern[beat % 16];
            
            // Velocity can add extra triggers
            const velocityBonus = modulation.velocity * CONFIG.melodicPhysics.velocityTriggerBonus;
            const shouldTrigger = baseTrigger || (dynamicProb > 0 && Math.random() < dynamicProb * velocityBonus);
            
            if (shouldTrigger) {
                this.trigger(time, modulation);
            }
        }
        
        // Handle call and response
        if (modulation.isResponding) {
            this.triggerResponse(time, modulation);
            this.isResponding = false;
        }
    }

    /**
     * Trigger a melodic response to a nearby node
     */
    triggerResponse(time, modulation) {
        if (!modulation.responseNote) return;
        
        time = time || audioEngine.now();
        this.pulse = 0.7;
        
        // Parse the heard note and create a harmonic response
        const responseNote = this.createHarmonicResponse(modulation.responseNote, modulation);
        
        if (responseNote) {
            this.lastNote = responseNote;
            this.addToMelodicMemory(responseNote);
            
            audioEngine.playNoteWithModulation(
                this.instDef.id,
                responseNote,
                this.getDuration(modulation),
                time,
                modulation
            );
        }
    }

    /**
     * Create a harmonically related response note
     */
    createHarmonicResponse(heardNote, modulation) {
        const scale = audioEngine.scale;
        if (!heardNote || scale.length === 0) return null;
        
        // Parse heard note
        const noteName = heardNote.slice(0, -1);
        const octave = parseInt(heardNote.slice(-1));
        
        // Find heard note in scale
        const baseIndex = scale.findIndex(n => n.startsWith(noteName));
        if (baseIndex === -1) return heardNote;
        
        // Choose a consonant interval based on movement and connections
        const intervals = CONFIG.melodicPhysics.responseIntervals;
        
        // Weight interval choice by melodic contour
        let intervalIndex;
        if (modulation.melodicContour > 0.3) {
            // Moving up - prefer ascending intervals
            intervalIndex = Math.floor(Math.random() * (intervals.length / 2)) + intervals.length / 2;
        } else if (modulation.melodicContour < -0.3) {
            // Moving down - prefer descending intervals
            intervalIndex = Math.floor(Math.random() * (intervals.length / 2));
        } else {
            intervalIndex = Math.floor(Math.random() * intervals.length);
        }
        
        const interval = intervals[Math.floor(intervalIndex)] || 0;
        const newIndex = (baseIndex + interval + scale.length) % scale.length;
        const octaveShift = Math.floor((baseIndex + interval) / scale.length);
        
        const newNote = scale[newIndex];
        if (newNote) {
            const newNoteName = newNote.slice(0, -1);
            const finalOctave = Math.max(2, Math.min(5, octave + octaveShift));
            return newNoteName + finalOctave;
        }
        
        return heardNote;
    }

    /**
     * Trigger note from the looping pattern - enhanced version
     */
    triggerFromPattern(time, modulation) {
        time = time || audioEngine.now();
        modulation = modulation || this.getPhysicsModulation();
        
        const pattern = CONFIG.patterns[this.patternName];
        if (!pattern) return;
        
        // Calculate pattern index with multiple influences
        let patternIdx = this.patternIndex;
        
        // Connection-based pattern shift
        patternIdx = (patternIdx + modulation.patternShift) % pattern.length;
        
        // Melodic memory can influence pattern choice
        if (this.melodicMemory.length > 2 && Math.random() < 0.2) {
            // Occasionally jump based on memory
            patternIdx = (patternIdx + this.melodicMemory.length) % pattern.length;
        }
        
        const patternValue = pattern[patternIdx];
        
        // Advance pattern
        this.patternIndex = (this.patternIndex + 1) % pattern.length;
        
        // Handle rest (-1) with velocity-based skip
        if (patternValue === -1) {
            // High velocity can override rests occasionally
            if (modulation.velocity > 0.8 && Math.random() < 0.3) {
                // Play a passing note instead
                this.triggerPassingNote(time, modulation);
            }
            return;
        }
        
        this.pulse = 1;
        
        // Get base note with enhanced position mapping
        const baseNote = this.getPositionNote(modulation);
        
        // Handle chord patterns
        if (Array.isArray(patternValue)) {
            this.triggerChord(patternValue, baseNote, time, modulation);
        } else {
            const note = this.getEnhancedNote(baseNote, patternValue, modulation);
            // MetalSynth and NoiseSynth don't need notes - always trigger
            const toneType = this.instDef.toneType;
            if (note || toneType === 'MetalSynth' || toneType === 'NoiseSynth') {
                this.lastNote = note || 'C4';
                this.addToMelodicMemory(this.lastNote);
                audioEngine.playNoteWithModulation(
                    this.instDef.id,
                    this.lastNote,
                    this.getDuration(modulation),
                    time,
                    modulation
                );
            }
        }
    }

    /**
     * Get note based on position with enhanced mapping
     */
    getPositionNote(modulation) {
        // Basic Y position mapping
        let baseNote = audioEngine.getNoteFromY(this.y, this.canvasHeight);
        
        // Apply connection pitch influence
        if (modulation.connectionPitchInfluence && modulation.connectionPitchInfluence.pitchCenter !== null) {
            const influence = CONFIG.melodicPhysics.connectionPitchPull;
            const blendedY = this.y * (1 - influence * modulation.connectionStrength) + 
                           modulation.connectionPitchInfluence.pitchCenter * influence * modulation.connectionStrength;
            baseNote = audioEngine.getNoteFromY(blendedY, this.canvasHeight);
        }
        
        return baseNote;
    }

    /**
     * Trigger a chord based on pattern
     */
    triggerChord(degrees, baseNote, time, modulation) {
        // Modify chord voicing based on connections
        const modifiedDegrees = [...degrees];
        
        // Add extensions based on connection count
        if (modulation.connections > 0.5 && modifiedDegrees.length < 4) {
            // Add a 7th or 9th
            const extension = modulation.connections > 0.7 ? 8 : 6;
            if (!modifiedDegrees.includes(extension)) {
                modifiedDegrees.push(extension);
            }
        }
        
        modifiedDegrees.forEach((degree, i) => {
            const note = this.getEnhancedNote(baseNote, degree, modulation);
            if (note) {
                this.lastNote = note;
                // Slight timing spread for natural feel
                const timeOffset = i * 0.01 * (1 - modulation.velocity);
                audioEngine.playNoteWithModulation(
                    this.instDef.id,
                    note,
                    this.getDuration(modulation),
                    time + timeOffset,
                    modulation
                );
            }
        });
        
        this.addToMelodicMemory(this.lastNote);
    }

    /**
     * Trigger a passing note (fills in rests at high velocity)
     */
    triggerPassingNote(time, modulation) {
        if (this.melodicMemory.length < 2) return;
        
        // Create a note that connects the last two notes
        const lastNote = this.melodicMemory[this.melodicMemory.length - 1];
        const prevNote = this.melodicMemory[this.melodicMemory.length - 2];
        
        const passingNote = this.getPassingNote(prevNote, lastNote);
        if (passingNote) {
            this.pulse = 0.5;
            this.lastNote = passingNote;
            audioEngine.playNoteWithModulation(
                this.instDef.id,
                passingNote,
                '16n', // Short passing note
                time,
                { ...modulation, volumeMod: modulation.volumeMod * 0.6 }
            );
        }
    }

    /**
     * Get a note that passes between two notes
     */
    getPassingNote(from, to) {
        if (!from || !to) return null;
        
        const scale = audioEngine.scale;
        const fromName = from.slice(0, -1);
        const toName = to.slice(0, -1);
        
        const fromIdx = scale.findIndex(n => n.startsWith(fromName));
        const toIdx = scale.findIndex(n => n.startsWith(toName));
        
        if (fromIdx === -1 || toIdx === -1) return null;
        
        // Get the middle note
        const midIdx = Math.floor((fromIdx + toIdx) / 2);
        if (midIdx === fromIdx || midIdx === toIdx) return null;
        
        const midNote = scale[midIdx];
        if (midNote) {
            const midName = midNote.slice(0, -1);
            const octave = parseInt(from.slice(-1));
            return midName + octave;
        }
        
        return null;
    }

    /**
     * Get enhanced note with physics-based modifications
     */
    getEnhancedNote(baseNote, scaleDegree, modulation) {
        const scale = audioEngine.scale;
        if (!baseNote || scale.length === 0) return baseNote;
        
        const noteName = baseNote.slice(0, -1);
        const octave = parseInt(baseNote.slice(-1));
        
        // Apply melodic contour influence
        let contourOffset = 0;
        if (Math.abs(modulation.melodicContour) > 0.3) {
            contourOffset = Math.sign(modulation.melodicContour) * 
                           Math.floor(Math.abs(modulation.melodicContour) * 2);
        }
        
        // Apply scale degree offset from X position
        const positionOffset = modulation.scaleDegreeOffset || 0;
        
        // Apply harmonic gravity weighting
        let gravityAdjustedDegree = scaleDegree;
        if (modulation.harmonicGravity && Math.random() < CONFIG.melodicPhysics.harmonicGravityStrength) {
            const weights = modulation.harmonicGravity;
            const normalizedDegree = ((scaleDegree % scale.length) + scale.length) % scale.length;
            
            // If current degree has low gravity weight, pull toward nearby high-weight degree
            if (weights[normalizedDegree] < 0.5) {
                // Find nearest high-weight degree
                for (let offset = 1; offset < 3; offset++) {
                    const upDegree = (normalizedDegree + offset) % scale.length;
                    const downDegree = (normalizedDegree - offset + scale.length) % scale.length;
                    
                    if (weights[upDegree] > 0.7) {
                        gravityAdjustedDegree = scaleDegree + offset;
                        break;
                    }
                    if (weights[downDegree] > 0.7) {
                        gravityAdjustedDegree = scaleDegree - offset;
                        break;
                    }
                }
            }
        }
        
        // Combine all modifications
        const totalDegreeOffset = gravityAdjustedDegree + contourOffset + positionOffset;
        
        // Apply velocity-based pitch shift
        const pitchSemitones = Math.round(modulation.pitchShift);
        
        // Apply connection-based octave shift
        const connectionOctaveShift = modulation.octaveShift;
        
        // Calculate final note
        const baseIndex = scale.findIndex(n => n.startsWith(noteName));
        const newIndex = ((baseIndex + totalDegreeOffset) % scale.length + scale.length) % scale.length;
        
        const newNote = scale[newIndex];
        if (newNote) {
            const newNoteName = newNote.slice(0, -1);
            const octaveOffset = Math.floor((baseIndex + totalDegreeOffset) / scale.length);
            const finalOctave = Math.max(1, Math.min(6, 
                octave + connectionOctaveShift + octaveOffset + Math.floor(pitchSemitones / 12)
            ));
            return newNoteName + finalOctave;
        }
        
        return baseNote;
    }

    /**
     * Add note to melodic memory
     */
    addToMelodicMemory(note) {
        if (!note) return;
        
        this.melodicMemory.push(note);
        if (this.melodicMemory.length > this.maxMelodicMemory) {
            this.melodicMemory.shift();
        }
    }

    /**
     * Get duration based on physics
     */
    getDuration(modulation) {
        let baseDur = '8n';
        
        if (this.instDef.behavior === 'floaty') baseDur = '2n';
        if (this.instDef.behavior === 'heavy') baseDur = '4n';
        if (this.instDef.toneType === 'MembraneSynth') baseDur = '16n';
        if (this.isLooping) baseDur = '8n';
        
        // Acceleration shortens notes
        if (modulation && modulation.durationMod < 0.7) {
            const durationMap = {
                '2n': '4n',
                '4n': '8n', 
                '8n': '16n',
                '16n': '32n'
            };
            baseDur = durationMap[baseDur] || baseDur;
        }
        
        return baseDur;
    }

    /**
     * Trigger the node's sound - enhanced version
     */
    trigger(time, modulation) {
        time = time || audioEngine.now();
        modulation = modulation || this.getPhysicsModulation();
        this.pulse = 1;
        
        // Enhanced note selection
        const note = this.getPositionNote(modulation);
        this.lastNote = note;
        this.addToMelodicMemory(note);
        
        // Determine pitch with drum special handling
        let pitch = note;
        if (this.instDef.toneType === 'MembraneSynth') {
            // Velocity affects drum pitch
            const velocityPitch = modulation.velocity > 0.6 ? "G2" : "C2";
            pitch = this.y > this.canvasHeight / 2 ? "C2" : velocityPitch;
        }

        // Use modulated playback
        audioEngine.playNoteWithModulation(
            this.instDef.id, 
            pitch, 
            this.getDuration(modulation), 
            time, 
            modulation
        );
        
        // Collision harmony - play harmonically related note if colliding
        if (modulation.collisionTriggered && modulation.collisionPartner) {
            this.triggerCollisionHarmony(time, modulation);
        }
    }

    /**
     * Trigger additional harmony note on collision
     */
    triggerCollisionHarmony(time, modulation) {
        const partner = modulation.collisionPartner;
        if (!partner || !partner.lastNote) return;
        
        // Create a harmonically related note
        const harmonyNote = this.createHarmonicResponse(partner.lastNote, modulation);
        
        if (harmonyNote && harmonyNote !== this.lastNote) {
            // Play with slight delay for natural feel
            audioEngine.playNoteWithModulation(
                this.instDef.id,
                harmonyNote,
                '16n',
                time + 0.05,
                { ...modulation, volumeMod: modulation.volumeMod * 0.5 }
            );
        }
    }

    /**
     * Check if a point is inside this node
     */
    containsPoint(x, y) {
        const dx = x - this.x;
        const dy = y - this.y;
        return Math.sqrt(dx * dx + dy * dy) < this.radius;
    }
}